import React from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";

const Table_Alignment = () => {
  return (
    <div className="tutorial-container">
      <h1 className="tutorial-title">📐 CSS Table Alignment</h1>

      <p className="tutorial-intro">
        CSS provides properties to align text and content inside table cells.
        You can align content horizontally using <code>text-align</code> and
        vertically using <code>vertical-align</code>.
      </p>

      {/* Highlight Box */}
      <div
        style={{
          background: "linear-gradient(135deg, #e0f2fe, #bae6fd)",
          padding: "1rem",
          borderRadius: "12px",
          marginBottom: "2rem",
          textAlign: "center",
        }}
      >
        <h2 style={{ color: "#075985", marginBottom: "0.5rem" }}>
          ✨ Why Use Alignment in Tables?
        </h2>
        <p style={{ fontSize: "1.1rem", lineHeight: "1.6" }}>
          Proper alignment makes tables easier to read. It helps organize text,
          numbers, and other content neatly inside cells.
        </p>
      </div>

      {/* Horizontal Alignment */}
      <div className="tutorial-section">
        <h2 className="section-title">🔹 Horizontal Alignment</h2>
        <p>
          Use <code>text-align</code> to align text horizontally inside{" "}
          <code>&lt;th&gt;</code> or <code>&lt;td&gt;</code>.
        </p>

        <CodeCopyBlock
          code={`td {
  text-align: center;  /* values: left | center | right */
}`}
        />

        <div style={{ marginTop: "1rem" }}>
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              border: "1px solid black",
            }}
          >
            <thead>
              <tr>
                <th style={{ border: "1px solid black", padding: "8px" }}>
                  Left Align
                </th>
                <th style={{ border: "1px solid black", padding: "8px" }}>
                  Center Align
                </th>
                <th style={{ border: "1px solid black", padding: "8px" }}>
                  Right Align
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td
                  style={{
                    border: "1px solid black",
                    padding: "8px",
                    textAlign: "left",
                  }}
                >
                  Apple
                </td>
                <td
                  style={{
                    border: "1px solid black",
                    padding: "8px",
                    textAlign: "center",
                  }}
                >
                  Banana
                </td>
                <td
                  style={{
                    border: "1px solid black",
                    padding: "8px",
                    textAlign: "right",
                  }}
                >
                  Cherry
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Vertical Alignment */}
      <div className="tutorial-section">
        <h2 className="section-title">🔹 Vertical Alignment</h2>
        <p>
          Use <code>vertical-align</code> to align content vertically inside a
          cell.
        </p>

        <CodeCopyBlock
          code={`td {
  height: 60px;
  vertical-align: middle;  /* values: top | middle | bottom */
}`}
        />

        <div style={{ marginTop: "1rem" }}>
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              border: "1px solid black",
            }}
          >
            <thead>
              <tr>
                <th style={{ border: "1px solid black", padding: "8px" }}>
                  Top
                </th>
                <th style={{ border: "1px solid black", padding: "8px" }}>
                  Middle
                </th>
                <th style={{ border: "1px solid black", padding: "8px" }}>
                  Bottom
                </th>
              </tr>
            </thead>
            <tbody>
              <tr style={{ height: "80px" }}>
                <td
                  style={{
                    border: "1px solid black",
                    verticalAlign: "top",
                    padding: "8px",
                  }}
                >
                  Aligned Top
                </td>
                <td
                  style={{
                    border: "1px solid black",
                    verticalAlign: "middle",
                    padding: "8px",
                  }}
                >
                  Aligned Middle
                </td>
                <td
                  style={{
                    border: "1px solid black",
                    verticalAlign: "bottom",
                    padding: "8px",
                  }}
                >
                  Aligned Bottom
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Combine Horizontal + Vertical */}
      <div className="tutorial-section">
        <h2 className="section-title">🔹 Combine Horizontal & Vertical</h2>
        <p>
          You can use both <code>text-align</code> and{" "}
          <code>vertical-align</code> together to control exact positioning.
        </p>

        <CodeCopyBlock
          code={`td {
  text-align: center;
  vertical-align: middle;
}`}
        />

        <div style={{ marginTop: "1rem" }}>
          <table
            style={{
              width: "100%",
              height: "100px",
              borderCollapse: "collapse",
              border: "1px solid black",
            }}
          >
            <tbody>
              <tr>
                <td
                  style={{
                    border: "1px solid black",
                    textAlign: "center",
                    verticalAlign: "middle",
                  }}
                >
                  Perfectly Centered
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Next Steps */}
      <div className="tutorial-section">
        <h2 className="section-title">🚀 Next Steps</h2>
        <p>
          Now that you know how to align table content, explore{" "}
          <a href="/css/table-padding" className="learn-more">
            Table Padding
          </a>{" "}
          to add spacing inside your table cells.
        </p>
      </div>
    </div>
  );
};

export default Table_Alignment;
